import React from 'react'
import { Loading, Message } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useIndeedFlexCompaniesQuery } from 'queries/admin/indeedFlex/useIndeedFlexCompaniesQuery'
import { useIndeedFlexClientsQuery } from 'queries/admin/indeedFlex/useIndeedFlexClientsQuery'
import { useCompaniesQuery } from 'queries/companies'

type IndeedFlexCompaniesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useIndeedFlexCompaniesQuery>['data']>
    clients: NonNullable<
      ReturnType<typeof useIndeedFlexClientsQuery>['data']
    >['items']
    companies: NonNullable<ReturnType<typeof useCompaniesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
  }
  actions: {
    refetch: () => void
  }
}

const [Context, useIndeedFlexCompaniesContextValue] =
  createContext<IndeedFlexCompaniesContextState>({
    name: 'IndeedFlexCompaniesContext',
  })

export const IndeedFlexCompaniesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useIndeedFlexCompaniesQuery()
  const { data: clientsData } = useIndeedFlexClientsQuery()
  const { data: companiesData } = useCompaniesQuery()

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value: IndeedFlexCompaniesContextState = {
    state: {
      data,
      clients: clientsData?.items ?? [],
      companies: companiesData ?? [],
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error) ?? '',
    },
    actions: {
      refetch,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useIndeedFlexCompaniesContextValue }
